import { useEffect, useState } from "react";
import { X, Bot, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function RunAgentModal({ deal, onClose, onCompleted }) {
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/agents").then(({ data }) => {
      setAgents(data.filter((a) => a.is_published && a.is_active));
    });
  }, []);

  const run = async () => {
    if (!selected) return;
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post(`/deals/${deal.deal_id}/agent-runs`, {
        agent_id: selected.agent_id,
      });
      setResult(data);
      toast.success(`Agent '${selected.agent_name}' completed`);
      onCompleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Agent run failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="run-agent-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "var(--cw-border)" }}>
          <h2 className="cw-h2 inline-flex items-center gap-2">
            <Bot size={20} /> Run Agent — {deal.company_name}
          </h2>
          <button onClick={onClose} data-testid="close-run-agent"><X size={20} /></button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {!selected ? (
            <>
              <div className="cw-caption mb-3">Choose an agent to run against this deal</div>
              {agents.length === 0 && (
                <div className="text-sm text-[var(--cw-muted)] italic">
                  No published agents available. Create one in Agent Library.
                </div>
              )}
              <div className="space-y-2">
                {agents.map((a) => (
                  <button key={a.agent_id} onClick={() => setSelected(a)}
                    className="w-full text-left cw-card p-4 hover:border-[var(--cw-primary)] transition"
                    data-testid={`select-agent-${a.agent_name}`}>
                    <div className="font-semibold">{a.agent_name}</div>
                    <div className="text-xs text-[var(--cw-muted)] mt-1">
                      {a.agent_type} · {a.model_provider} · {a.model_name}
                    </div>
                    {a.description && (
                      <div className="text-xs text-[var(--cw-muted)] mt-2">{a.description}</div>
                    )}
                  </button>
                ))}
              </div>
            </>
          ) : running ? (
            <div className="flex flex-col items-center py-12">
              <Loader2 size={40} className="animate-spin" style={{ color: "var(--cw-primary)" }} />
              <div className="mt-4 text-sm font-medium">Running {selected.agent_name}…</div>
              <div className="cw-caption mt-1">Calling {selected.model_provider} · {selected.model_name}</div>
              <div className="cw-caption mt-3 max-w-sm text-center">
                This may take 5-30 seconds. The result will be saved to this deal&apos;s Agent Outputs tab.
              </div>
            </div>
          ) : result ? (
            <>
              <div className="cw-caption uppercase tracking-widest">Result</div>
              <h3 className="cw-h3 mt-1">{selected.agent_name}</h3>
              <div
                className="mt-4 p-4 rounded-md text-sm whitespace-pre-wrap"
                style={{ background: "#F6F6F7", color: "var(--cw-body)" }}
                data-testid="agent-run-output"
              >
                {result.output_text}
              </div>
            </>
          ) : (
            <>
              <button onClick={() => setSelected(null)} className="cw-btn-ghost text-xs mb-4">
                ← Choose a different agent
              </button>
              <div className="font-semibold">{selected.agent_name}</div>
              <div className="text-xs text-[var(--cw-muted)] mt-1">
                {selected.agent_type} · {selected.model_provider} · {selected.model_name}
              </div>
              {selected.description && (
                <p className="text-sm text-[var(--cw-muted)] mt-3">{selected.description}</p>
              )}
              <div className="mt-4 cw-caption">System Prompt Preview</div>
              <div className="mt-1 p-3 rounded-md text-xs font-mono max-h-32 overflow-y-auto"
                style={{ background: "#F6F6F7", color: "var(--cw-body)" }}>
                {selected.system_prompt}
              </div>
            </>
          )}
        </div>

        {selected && !result && (
          <div className="border-t px-6 py-4 flex justify-between" style={{ borderColor: "var(--cw-border)" }}>
            <button onClick={() => setSelected(null)} disabled={running} className="cw-btn-ghost">
              Back
            </button>
            <button onClick={run} disabled={running} className="cw-btn-primary inline-flex items-center gap-2"
              data-testid="submit-agent-run-btn">
              {running ? <><Loader2 size={14} className="animate-spin" /> Running…</> : <>Run Agent</>}
            </button>
          </div>
        )}
        {result && (
          <div className="border-t px-6 py-4 flex justify-end" style={{ borderColor: "var(--cw-border)" }}>
            <button onClick={onClose} className="cw-btn-primary" data-testid="close-agent-result-btn">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
